import { useEffect, useRef } from "react";

type EscapeHandler = {
  onEscape: () => void;
};

const escapeStack: EscapeHandler[] = [];
let listening = false;

function handleGlobalKeyDown(event: KeyboardEvent) {
  if (event.key !== "Escape") return;
  const handler = escapeStack[escapeStack.length - 1];
  if (!handler) return;
  event.preventDefault();
  event.stopPropagation();
  handler.onEscape();
}

function ensureGlobalListener() {
  if (listening) return;
  document.addEventListener("keydown", handleGlobalKeyDown);
  listening = true;
}

function removeGlobalListenerIfIdle() {
  if (!listening || escapeStack.length > 0) return;
  document.removeEventListener("keydown", handleGlobalKeyDown);
  listening = false;
}

export function useEscapeKey(
  onEscape: () => void,
  enabled: boolean = true,
): void {
  const onEscapeRef = useRef(onEscape);

  useEffect(() => {
    onEscapeRef.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    if (!enabled) return;
    const handler: EscapeHandler = {
      onEscape: () => onEscapeRef.current(),
    };
    escapeStack.push(handler);
    ensureGlobalListener();
    return () => {
      const index = escapeStack.indexOf(handler);
      if (index !== -1) escapeStack.splice(index, 1);
      removeGlobalListenerIfIdle();
    };
  }, [enabled]);
}
